import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Grid, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom';
import ProposalCard from '../atoms/ProposalCard/ProposalCard'
import ProposalTsb from './ProposalTsb'
import ProposalTable from './ProposalTable'
import CommonModal from '../atoms/modal/CommonModal'
import ButtonCustom from '../atoms/button/ButtonCustom'
import TextArea from '../atoms/textarea/TextArea'
import FileAttach from '../atoms/file-attach/FileAttach'
import CommonDeleteModal from '../atoms/modal/CommonDeleteModal'
import BidDetailsCard from '../atoms/BidDetailsCard/BidDetailsCard'
import FilterPopover from '../dataGrid/FilterPopover'

import styles from './ProposalPageScreen.module.scss'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { getAllProposals, makeBid } from '../../features/proposalSlice'
import { closeModal, openModal, setModalData } from '../../features/commonSlice'
import { formatCommonDate } from '../../util/formatDateAndTime'
import InputCustom from '../atoms/input/InputCustom'
import { toastHelper } from '../../util/toastHelper'
import DateCustom from '../atoms/input/DateCustom'
import MultiselectSearch from '../atoms/select/MultiSelect'
import { formatPackageData, totalFromTree } from './modal/reviewLineItemTree'

function ProposalPageScreen() {
	const dispatch = useDispatch()
	const isSidebarOpen = useSelector((state) => state.sidebar.isSidebarOpen)
	const services = useSelector((state) => state.auth.user?.services)
	const modal = useSelector((state) => state.common.modal)
	const modalData = useSelector((state) => state.common.data)
	const proposals = useSelector((state) => state.proposal.proposals)

	const [bidData, setBidData] = useState({
		amount: 0,
		note: '',
		services: [],
		paymentDueDate: null,
	})
	const [searchParams, setSearchParams] = useSearchParams()
	const tab = searchParams.get('tab')
	const [value, setValue] = useState(0)
	const [innerTab, setInnerTab] = useState(0)
	const [search, setSearch] = useState('')
	const [filterAnchor, setFilterAnchor] = useState(null)
	const [statusFilter, setStatusFilter] = useState([])
	const [responseFilter, setResponseFilter] = useState([])

	const filteredRows = useMemo(() => {
		if (!proposals?.length) return []

		const query = search.trim().toLowerCase()
		return proposals
			.filter((row) => {
				if (value === 0 && row.status !== 'New') return false
				else if (value === 1 && row.status === 'New') return false
				// Search filter
				if (query && !row[kazooName].toLowerCase().includes(query))
					return false

				// Response filter
				if (
					responseFilter.length &&
					!responseFilter.includes(row.responseLabel)
				)
					return false

				// Status filter (only for my bids)
				if (
					value === 1 &&
					statusFilter.length &&
					!statusFilter.includes(row.label)
				)
					return false

				return true
			})
			?.map((item) => {
				// const { grandTotal } = formatPackageData(item.packageDetail) item.bidAmount || grandTotal ||
				return { ...item, price: item.userBudget }
			})
	}, [value, search, statusFilter, responseFilter, proposals])

	const vendorServices = useMemo(() => {
		if (!services?.length) return []
		return services.map((e, i) => ({ _id: i + 1, name: e }))
	}, [services])

	const openFilter = (e) => setFilterAnchor(e.currentTarget)
	const closeFilter = () => setFilterAnchor(null)
	const clearFilters = () => {
		setStatusFilter([])
		setResponseFilter([])
	}

	const handleChange = (event, newValue) => {
		setValue(newValue)
		setSearch('')
		clearFilters()
		if (!!tab) setSearchParams('')
	}
	useEffect(() => {
		if (tab) setValue(1)
	}, [tab])
	const handleChangeListCalendar = (event, newValue) => {
		setInnerTab(newValue)
	}

	const handleSubmit = (e) => {
		e.preventDefault()
		if (!bidData.amount) {
			toastHelper('Please enter a bid amount', 'error')
			return
		}
		if (!bidData.paymentDueDate) {
			toastHelper('Please select a due date', 'error')
			return
		}

		const now = new Date()

		const dueDate =
			typeof bidData.paymentDueDate?.toDate === 'function'
				? bidData.paymentDueDate.toDate()
				: new Date(bidData.paymentDueDate)

		if (isNaN(dueDate.getTime()) || dueDate <= now) {
			toastHelper('Payment due date must be in the future', 'error')
			return
		}
		if (!bidData?.services?.length) {
			toastHelper('Please select at least one service', 'error')
			return
		}
		dispatch(openModal('bidDetails'))
	}

	const closeAllModals = () => {
		dispatch(closeModal())
		dispatch(setModalData(null))
		setBidData({ amount: 0, note: '', services: [], paymentDueDate: null })
	}

	const handleSubmitConfirm = (e) => {
		e.preventDefault()
		dispatch(
			makeBid({
				proposalId: modalData.proposalId,
				amount: bidData.amount,
				note: bidData.note,
				services: bidData.services.map((s) => s.name),
				paymentDueDate: bidData.paymentDueDate
					? bidData.paymentDueDate.toISOString?.()
					: null,
			})
		)
		closeAllModals()
	}

	useEffect(() => {
		dispatch(getAllProposals())
	}, [dispatch])
	const handleMultiSelect = (selectedService) =>
		setBidData((prev) => ({
			...prev,
			services: [...prev.services, selectedService],
		}))

	const handleMultiRemove = (selectedServiceId) =>
		setBidData((prev) => ({
			...prev,
			services: prev.services.filter(
				(service) => service._id !== selectedServiceId
			),
		}))

	return (
		<>
			<div className={styles.pageHeader}>
				<Typography component="h1">Proposals</Typography>
				<div className={styles.right}>
					<div className={styles.date}>
						<img src="/images/calendar_line.svg" alt="calendar" />
						<span>
							{formatCommonDate(new Date().toISOString())}
						</span>
					</div>
				</div>
			</div>
			<div className={styles.ProposalPageScreenMain}>
				<ProposalTsb
					ProposalCardDataGrid={
						<div
							className={`${styles.ProposalPageScreen} ${isSidebarOpen ? styles.sidebarExpand : ''}`}
						>
							{filteredRows.map((item, index) => (
								<ProposalCard
									key={index}
									proposalId={item.proposalId}
									image={item.image}
									packageData={item.lineItems}
									responseLabel={item.responseLabel}
									title={item.kazooName}
									date={item.date}
									guests={item.guests}
									service={item.category}
									description={item.subCategory}
									price={item.price}
									rawData={item}
									StatusLabel={item.status}
								/>
							))}
						</div>
					}
					ProposalCardDataList={
						<div className={`${styles.ProposalPageScreenList}`}>
							<ProposalTable rows={filteredRows} />
						</div>
					}
					ProposalCardBidDataGrid={
						<div
							className={`${styles.ProposalPageScreen} ${isSidebarOpen ? styles.sidebarExpand : ''}`}
						>
							{filteredRows.map((item, index) => (
								<ProposalCard
									ViewDetail={false}
									StatusLabel={item.status}
									key={index}
									proposalId={item.proposalId}
									image={item.image}
									packageData={item.lineItems}
									responseLabel={item.responseLabel}
									title={item.kazooName}
									date={item.date}
									guests={item.guests}
									service={item.category}
									description={item.subCategory}
									price={item.price}
									rawData={item}
								/>
							))}
						</div>
					}
					ProposalCardBidDataList={
						<div className={`${styles.ProposalPageScreenList}`}>
							<ProposalTable
								rows={filteredRows}
								makeBid={false}
							/>
						</div>
					}
					value={value}
					handleChange={handleChange}
					innerTab={innerTab}
					handleChangeListCalendar={handleChangeListCalendar}
					onClickFilter={openFilter}
					searchQuery={search}
					onSearchChange={setSearch}
				/>
				<FilterPopover
					anchorEl={filterAnchor}
					open={Boolean(filterAnchor)}
					onClose={closeFilter}
					onClear={clearFilters}
					onApply={closeFilter}
					sections={[
						{
							id: 'responseLabel',
							title: 'Response Label',
							options: [
								'Very Responsive',
								'Responsive',
								'Neutral',
								'Less Responsive',
								'Unresponsive',
							],
							selected: responseFilter,
							onToggle: (val, checked) =>
								setResponseFilter((prev) =>
									checked
										? [...prev, val]
										: prev.filter((x) => x !== val)
								),
						},
						...(value === 1
							? [
									{
										id: 'label',
										title: 'Status',
										options: [
											'Submitted',
											'Approved',
											'Declined',
											'Rejected',
											'Paid',
										],
										selected: statusFilter,
										onToggle: (val, checked) =>
											setStatusFilter((prev) =>
												checked
													? [...prev, val]
													: prev.filter(
															(x) => x !== val
														)
											),
									},
								]
							: []),
					]}
				/>
			</div>

			{/* Create Modal */}
			<CommonModal
				open={modal === 'makeBid'}
				handleClose={closeAllModals}
				heading="Make a Bid"
				footerContent={
					<>
						<ButtonCustom
							onClick={closeAllModals}
							variant="outlined"
							size="lg"
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom
							variant="default"
							size="lg"
							type="submit"
							form="bidForm"
						>
							Submit Bid
						</ButtonCustom>
					</>
				}
			>
				<form
					id="bidForm"
					onSubmit={handleSubmit}
					className={styles.formWrp}
				>
					<Grid container spacing={1.5}>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label
									htmlFor="amount"
									className={styles.label}
								>
									Bid Amount
								</label>
								<InputCustom
									type="number"
									name="amount"
									id="amount"
									placeholder="Enter Amount"
									className={styles.taskTitleInput}
									value={bidData.amount}
									data={bidData}
									setDataFunction={setBidData}
									inputProps={{ min: 0 }}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label className={styles.label}>Due Date</label>
								<DateCustom
									name="paymentDueDate"
									value={bidData.paymentDueDate}
									data={bidData}
									setDataFunction={setBidData}
									data_cy="bid-due-date"
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<MultiselectSearch
									members={vendorServices}
									selected={bidData.services}
									onSelect={handleMultiSelect}
									onRemove={handleMultiRemove}
									label="Add a service"
									placeholder="eg. food & beverages"
									searchPlaceholder="Search services..."
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label
									htmlFor="Status"
									className={styles.label}
								>
									Notes
								</label>
								<TextArea
									placeholder="Add Notes or customizations"
									value={bidData.note}
									onChange={(e) =>
										setBidData((prev) => ({
											...prev,
											note: e.target.value,
										}))
									}
								/>
							</div>
						</Grid>

						{/* TODO:: Attach files require flow */}
						{/* <Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label
									htmlFor="Attachment"
									className={styles.label}
								>
									Attachment
								</label>
								<FileAttach
									label="Choose File"
									placeholder="No Files Selected"
									accept=".jpg,.png,.pdf"
									multiple
									onChange={(e) =>
										console.log(e.target.files)
									}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<ButtonCustom
									variant="outlined"
									size="lg"
									className={styles.fileUploadedList}
								>
									<img src="/images/document.svg" />
									<Typography variant="body">
										Task Details.pdf
									</Typography>
									<img src="/images/file-close-icon-black.svg" />
								</ButtonCustom>
							</div>
						</Grid> */}
					</Grid>
				</form>
			</CommonModal>

			{/* Detail Modal */}
			<CommonDeleteModal
				open={modal === 'bidDetails'}
				handleClose={closeAllModals}
				heading="Bid Confirm"
				subHeading="Are you sure you want to make bid?"
				modalBody={styles.modalBody}
				className={styles.deleteModal}
				ClassIcon={styles.detailModalIcon}
				Icon={'/images/Bid.svg'}
				footerContent={
					<>
						<ButtonCustom
							onClick={closeAllModals}
							variant="outlined"
							size="lg"
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom
							variant="default"
							size="lg"
							type="submit"
							form="bidConfirmForm"
						>
							Confirm
						</ButtonCustom>
					</>
				}
			>
				<form
					id="bidConfirmForm"
					onSubmit={handleSubmitConfirm}
					className={styles.formBox}
				>
					<div className="">
						<BidDetailsCard icon={false} {...bidData} />
					</div>
				</form>
			</CommonDeleteModal>
		</>
	)
}

export default ProposalPageScreen
